import {
  fetchTagsConfig,
  renderBadges,
  type Badge,
  type FetchJson,
} from "./badgesShared";

const SEARCHSPRING_API_URL =
  "https://api.searchspring.net/api/search/search.json";
const SEARCHSPRING_FETCH_TIMEOUT_MS = 8000;
const CARD_BADGES_ENTITY = "ss-card-badges";

type SearchspringResult = { badges?: Badge[]; handle?: string };
type SearchspringResponse = { results?: SearchspringResult[] };

type MountBadgesConfig = {
  siteId: string;
  cards: NodeListOf<HTMLElement>;
  getHandleFromCard: (card: HTMLElement) => string;
  getBadgesHost?: (card: HTMLElement) => HTMLElement | null;
  shouldSkipCard?: (card: HTMLElement) => boolean;
  badgesEntity?: string;
};

const isSoldOutBadge = (badge: Badge): boolean => {
  const normalizedTag = (badge.tag ?? "").trim().toLowerCase();
  const normalizedValue = (badge.value ?? "").trim().toLowerCase();

  return (
    normalizedTag === "sold-out" ||
    normalizedTag === "soldout" ||
    normalizedTag.includes("sold out") ||
    normalizedValue.includes("sold out")
  );
};

const isAvailableBadge = (badge: Badge): boolean => !isSoldOutBadge(badge);

const fetchSearchspringJson: FetchJson = (url: string) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    SEARCHSPRING_FETCH_TIMEOUT_MS,
  );

  return fetch(url, { credentials: "omit", signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => window.clearTimeout(timeoutId));
};

async function fetchBadgesForHandles(
  siteId: string,
  handles: string[],
): Promise<Map<string, Badge[]>> {
  if (!handles.length) return new Map();

  const queryParams = new URLSearchParams();
  queryParams.set("siteId", siteId);
  queryParams.set("resultsFormat", "native");
  queryParams.set("resultsPerPage", String(handles.length));

  const handleFilterParam = "filter.handle";
  handles.forEach((handle) => queryParams.append(handleFilterParam, handle));

  const requestUrl = `${SEARCHSPRING_API_URL}?${queryParams.toString()}`;

  const responseData =
    await fetchSearchspringJson<SearchspringResponse>(requestUrl);
  const searchResults = responseData?.results ?? [];

  const badgesByHandle = new Map<string, Badge[]>();
  searchResults.forEach((result) => {
    const handle =
      typeof result.handle === "string" ? result.handle.trim() : "";
    if (!handle || badgesByHandle.has(handle)) return;
    badgesByHandle.set(
      handle,
      Array.isArray(result.badges) ? result.badges : [],
    );
  });

  return badgesByHandle;
}

function getOrCreateBadgesRoot(
  parent: HTMLElement,
  badgesSelector: string,
  badgesEntity: string,
): HTMLElement {
  const existingBadgesRoot = parent.querySelector(badgesSelector);
  if (existingBadgesRoot instanceof HTMLElement) return existingBadgesRoot;

  const badgesRootElement = document.createElement("div");
  badgesRootElement.dataset.entity = badgesEntity;
  badgesRootElement.className = "ss__pdp-badges";
  badgesRootElement.setAttribute("aria-live", "polite");
  parent.insertBefore(badgesRootElement, parent.firstChild);
  return badgesRootElement;
}

export async function mountSearchspringBadges({
  siteId,
  cards,
  getHandleFromCard,
  getBadgesHost,
  shouldSkipCard,
  badgesEntity = CARD_BADGES_ENTITY,
}: MountBadgesConfig): Promise<void> {
  const normalizedSiteId = siteId.trim();
  if (!normalizedSiteId) return;
  if (!cards.length) return;

  const handles: string[] = [];
  const seen = new Set<string>();

  cards.forEach((card) => {
    if (shouldSkipCard?.(card)) return;
    const handle = getHandleFromCard(card).trim();
    if (!handle || seen.has(handle)) return;
    seen.add(handle);
    handles.push(handle);
  });

  if (!handles.length) return;

  const badgesSelector = `[data-entity="${badgesEntity}"]`;

  const [badgesByHandle, tagsConfig] = await Promise.all([
    fetchBadgesForHandles(normalizedSiteId, handles),
    fetchTagsConfig(normalizedSiteId, fetchSearchspringJson),
  ]);
  const isCardBadges = badgesEntity === CARD_BADGES_ENTITY;

  cards.forEach((card) => {
    if (shouldSkipCard?.(card)) return;

    const handle = getHandleFromCard(card).trim();
    if (!handle) return;

    const host =
      getBadgesHost?.(card) ??
      card.querySelector<HTMLElement>(".card__inner, .card") ??
      card;

    const fetchedBadges = badgesByHandle.get(handle) ?? [];
    const productBadges = isCardBadges
      ? fetchedBadges.filter(isAvailableBadge)
      : fetchedBadges;
    if (!productBadges.length)
      return host.querySelector(badgesSelector)?.remove();

    const badgesRoot = getOrCreateBadgesRoot(
      host,
      badgesSelector,
      badgesEntity,
    );
    badgesRoot.replaceChildren();
    renderBadges(badgesRoot, productBadges, tagsConfig);
  });
}
